import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SquareLocation {
  id: string;
  name: string | null;
  currency: string | null;
  country: string | null;
  status: string | null;
}

export interface SquareProviderMetadata {
  merchant_id: string | null;
  business_name: string | null;
  merchant_country: string | null;
  merchant_currency: string | null;
  location_id: string | null;
  location_name: string | null;
  location_currency: string | null;
  available_locations: SquareLocation[];
}

export interface SquareIntegration {
  id: string;
  status: 'connected' | 'expired' | 'revoked' | 'error';
  status_message: string | null;
  environment: 'sandbox' | 'production';
  connected_at: string;
  last_refreshed_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  access_token_expires_at: string | null;
  token_valid: boolean;
  days_until_expiry: number | null;
  metadata: SquareProviderMetadata;
}

const QUERY_KEY = ['integration-status', 'square'] as const;

/** Sentinel returned by the status query when the RPC refuses a non-admin. */
const FORBIDDEN = Symbol('square-status-forbidden');
type StatusResult = SquareIntegration | null | typeof FORBIDDEN;

function isPermissionError(error: { message?: string } | null): boolean {
  const msg = error?.message ?? '';
  return /only admins/i.test(msg) || /permission denied/i.test(msg);
}

/**
 * Bring-your-own Square connection for online invoice payments.
 *
 * Mirrors useGoogleAdsConnection. The status RPC is admin-only and throws
 * for everyone else; that case is surfaced as `isForbidden` (with a null
 * integration) instead of an error so non-admin screens stay quiet.
 */
export function useSquareConnection() {
  const queryClient = useQueryClient();

  const statusQuery = useQuery<StatusResult>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_company_integration_status', {
        p_provider: 'square',
      });
      if (error) {
        if (isPermissionError(error)) return FORBIDDEN;
        throw error;
      }
      const row = (data ?? [])[0];
      if (!row) return null;
      const metadata = (row.provider_metadata ?? {}) as Partial<SquareProviderMetadata>;
      return {
        id: row.id,
        status: row.status as SquareIntegration['status'],
        status_message: row.status_message,
        environment: (row.environment === 'sandbox' ? 'sandbox' : 'production') as SquareIntegration['environment'],
        connected_at: row.connected_at,
        last_refreshed_at: row.last_refreshed_at,
        last_error: row.last_error,
        last_error_at: row.last_error_at,
        access_token_expires_at: row.access_token_expires_at,
        token_valid: Boolean(row.token_valid),
        days_until_expiry:
          row.days_until_expiry === null || row.days_until_expiry === undefined
            ? null
            : Number(row.days_until_expiry),
        metadata: {
          merchant_id: metadata.merchant_id ?? null,
          business_name: metadata.business_name ?? null,
          merchant_country: metadata.merchant_country ?? null,
          merchant_currency: metadata.merchant_currency ?? null,
          location_id: metadata.location_id ?? null,
          location_name: metadata.location_name ?? null,
          location_currency: metadata.location_currency ?? null,
          available_locations: Array.isArray(metadata.available_locations)
            ? metadata.available_locations
            : [],
        },
      };
    },
    // A permission refusal is deterministic; don't hammer the RPC.
    retry: (failureCount, error) => !isPermissionError(error as { message?: string }) && failureCount < 2,
  });

  // Initiate the OAuth flow. On success the browser is redirected to Square;
  // the user returns via /auth/square/callback (a SPA route) which forwards
  // the auth code to square-oauth-callback and then bounces them back to
  // /settings?square=... where the panel shows the result.
  const connectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('square-oauth-initiate', {
        body: { redirect_origin: window.location.origin },
      });
      if (error) throw new Error(error.message ?? 'Failed to start Square connection');
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error('Server did not return an authorization URL');
      window.location.href = url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('square-disconnect', { body: {} });
      if (error) throw new Error(error.message ?? 'Failed to disconnect');
      return data as { ok: boolean; revoked_at_square: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const setActiveLocationMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { data, error } = await supabase.functions.invoke('square-finalize-connect', {
        body: { location_id: locationId },
      });
      if (error) throw new Error(error.message ?? 'Failed to save location selection');
      return data as {
        ok: boolean;
        location_id: string;
        location_name: string | null;
        location_currency: string | null;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const isForbidden = statusQuery.data === FORBIDDEN;
  const integration: SquareIntegration | null =
    statusQuery.data && statusQuery.data !== FORBIDDEN ? statusQuery.data : null;

  return {
    integration,
    isForbidden,
    isLoading: statusQuery.isLoading,
    refetch: statusQuery.refetch,
    connect: connectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    setActiveLocation: setActiveLocationMutation.mutateAsync,
    isSettingLocation: setActiveLocationMutation.isPending,
  };
}
